/**
 * ReportMonthRangeSelector Component
 *
 * Month-range picker (From month/year → To month/year) for reports backed by
 * monthly data. Uses Month + Year selects rather than <input type="month">
 * (no Safari support). Values are YYYY-MM strings; range is capped at 12
 * months (the run-report API's 366-day limit).
 */

import React from 'react';

interface ReportMonthRangeSelectorProps {
  startMonth: string; // YYYY-MM
  endMonth: string;   // YYYY-MM
  onStartMonthChange: (month: string) => void;
  onEndMonthChange: (month: string) => void;
  disabled?: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** January of the current year — start of YTD. */
export function getDefaultStartMonth(): string {
  return `${new Date().getFullYear()}-01`;
}

/** Current month — end of YTD. */
export function getDefaultEndMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Inclusive month count between two YYYY-MM values (0 if reversed). */
export function monthSpan(startMonth: string, endMonth: string): number {
  const [sy, sm] = startMonth.split('-').map(Number);
  const [ey, em] = endMonth.split('-').map(Number);
  return (ey - sy) * 12 + (em - sm) + 1;
}

/** First day of the start month, YYYY-MM-DD. */
export function monthRangeToStartDate(startMonth: string): string {
  return `${startMonth}-01`;
}

/** Last day of the end month, YYYY-MM-DD. */
export function monthRangeToEndDate(endMonth: string): string {
  const [year, month] = endMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return `${endMonth}-${String(lastDay).padStart(2, '0')}`;
}

interface MonthYearPickerProps {
  id: string;
  label: string;
  value: string; // YYYY-MM
  onChange: (month: string) => void;
  disabled: boolean;
  invalid: boolean;
}

const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  id,
  label,
  value,
  onChange,
  disabled,
  invalid,
}) => {
  const [year, month] = value.split('-').map(Number);
  const currentYear = new Date().getFullYear();
  // Parts counter data starts in 2024; offer back to then.
  const years: number[] = [];
  for (let y = currentYear; y >= 2024; y--) years.push(y);

  const selectClass = `
    block w-full px-3 py-2 text-sm border rounded-md shadow-sm bg-white text-gray-900
    focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
    disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed
    ${invalid ? 'border-red-300' : 'border-gray-300'}
  `;

  return (
    <div>
      <label htmlFor={`${id}-month`} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="grid grid-cols-2 gap-2">
        <select
          id={`${id}-month`}
          aria-label={`${label} month`}
          value={month}
          onChange={(e) => onChange(`${year}-${String(e.target.value).padStart(2, '0')}`)}
          disabled={disabled}
          className={selectClass}
        >
          {MONTH_NAMES.map((name, i) => (
            <option key={name} value={i + 1}>{name}</option>
          ))}
        </select>
        <select
          id={`${id}-year`}
          aria-label={`${label} year`}
          value={year}
          onChange={(e) => onChange(`${e.target.value}-${String(month).padStart(2, '0')}`)}
          disabled={disabled}
          className={selectClass}
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

export const ReportMonthRangeSelector: React.FC<ReportMonthRangeSelectorProps> = ({
  startMonth,
  endMonth,
  onStartMonthChange,
  onEndMonthChange,
  disabled = false,
}) => {
  const span = monthSpan(startMonth, endMonth);
  const isReversed = span < 1;
  const isTooLong = span > 12;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <MonthYearPicker
          id="report-start-month"
          label="From Month"
          value={startMonth}
          onChange={onStartMonthChange}
          disabled={disabled}
          invalid={isReversed || isTooLong}
        />
        <MonthYearPicker
          id="report-end-month"
          label="To Month"
          value={endMonth}
          onChange={onEndMonthChange}
          disabled={disabled}
          invalid={isReversed || isTooLong}
        />
      </div>

      {isReversed && (
        <p className="text-sm text-red-600">To month must be on or after From month.</p>
      )}
      {isTooLong && (
        <p className="text-sm text-red-600">Month range must not exceed 12 months.</p>
      )}
    </div>
  );
};
